/* eslint-disable max-lines */
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { TextAreaFormik, TextInput, TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelect } from '@strata/react-selects';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useState } from 'react';

import { SeverityLevelsInfoModal } from './SeverityLevelsInfoModal';

import { handleError } from 'services/Error';

export type EditableFieldType =
    | 'name'
    | 'primaryImpactedServiceName'
    | 'description'
    | 'startedAt'
    | 'missingQualityCheck'
    | 'severity';

const OTHER_OPTION = 'Other';

interface EditIncidentFieldModalProps {
    field: EditableFieldType;
    fieldLabel: string;
    incident: {
        name: string;
        description: string;
        startedAt: string;
        missingQualityCheck: string;
        primaryImpactedServiceName: string;
        severity?: number;
    };
    isOpen: boolean;
    onClose: () => void;
    onSave: (field: EditableFieldType, value: string | number | undefined) => Promise<void>;
    missingQualityChecks: string[];
    impactedServiceNames: string[];
    severityLevels: { id: number; name: string }[];
    severityLevelNames: string[];
}

const getInitialSelectValue = (
    incident: EditIncidentFieldModalProps['incident'],
    field: EditableFieldType,
    options: string[],
) => {
    const value =
        field === 'missingQualityCheck'
            ? incident.missingQualityCheck
            : incident.primaryImpactedServiceName;
    return options.includes(value ?? '') ? value : OTHER_OPTION;
};

const getInitialOtherValue = (
    incident: EditIncidentFieldModalProps['incident'],
    field: EditableFieldType,
    options: string[],
) => {
    const value =
        field === 'missingQualityCheck'
            ? incident.missingQualityCheck
            : incident.primaryImpactedServiceName;
    return value && !options.includes(value) ? value : '';
};

const MissingQualityCheckForm = ({
    fieldLabel,
    incident: _incident,
    isOpen,
    onClose,
    onSave,
    missingQualityChecks,
    initialSelect,
    initialOther,
}: {
    fieldLabel: string;
    incident: EditIncidentFieldModalProps['incident'];
    isOpen: boolean;
    onClose: () => void;
    onSave: (f: EditableFieldType, v: string | number | undefined) => Promise<void>;
    missingQualityChecks: string[];
    initialSelect: string;
    initialOther: string;
}) => {
    const [selectValue, setSelectValue] = useState(initialSelect);
    const [otherValue, setOtherValue] = useState(initialOther);

    const valueToSave = selectValue === OTHER_OPTION ? otherValue : selectValue;
    const isSaveDisabled =
        !valueToSave?.trim() || (selectValue === OTHER_OPTION && !otherValue.trim());

    const handleSave = async () => {
        try {
            await onSave('missingQualityCheck', valueToSave || undefined);
            onClose();
        } catch (err) {
            handleError('Unable to update incident. Please try again.', err as Error);
        }
    };

    return (
        <Formik initialValues={{}} onSubmit={handleSave}>
            <DialogWithForm
                dialogTitle={`Edit ${fieldLabel}`}
                isOpen={isOpen}
                secondaryButtonLabel="Cancel"
                onSecondaryButtonClick={onClose}
                primaryButtonLabel="Save"
                isPrimaryButtonDisabled={isSaveDisabled}
                dialogContent={
                    <>
                        <SuggestSingleSelect
                            id="edit-missingQualityCheck"
                            name="edit-missingQualityCheck"
                            label={fieldLabel}
                            disableAutoPopulate
                            values={missingQualityChecks}
                            selectedItem={selectValue}
                            onChange={(val) => {
                                setSelectValue(val ?? '');
                                if (val !== OTHER_OPTION) setOtherValue('');
                            }}
                        />
                        {selectValue === OTHER_OPTION && (
                            <div style={{ marginTop: 16 }}>
                                <TextInput
                                    id="edit-missingQualityCheck-other"
                                    name="edit-missingQualityCheck-other"
                                    label="Specify other"
                                    placeholderText="Specify other"
                                    value={otherValue}
                                    onChange={(e) => setOtherValue(e.target.value)}
                                    fill
                                />
                            </div>
                        )}
                    </>
                }
            />
        </Formik>
    );
};

const PrimaryImpactedServiceForm = ({
    fieldLabel,
    incident: _incident,
    isOpen,
    onClose,
    onSave,
    impactedServiceNames,
    initialSelect,
    initialOther,
}: {
    fieldLabel: string;
    incident: EditIncidentFieldModalProps['incident'];
    isOpen: boolean;
    onClose: () => void;
    onSave: (f: EditableFieldType, v: string | number | undefined) => Promise<void>;
    impactedServiceNames: string[];
    initialSelect: string;
    initialOther: string;
}) => {
    const [selectValue, setSelectValue] = useState(initialSelect);
    const [otherValue, setOtherValue] = useState(initialOther);

    const valueToSave = selectValue === OTHER_OPTION ? otherValue : selectValue;
    const isSaveDisabled =
        !valueToSave?.trim() || (selectValue === OTHER_OPTION && !otherValue.trim());

    const handleSave = async () => {
        try {
            await onSave('primaryImpactedServiceName', valueToSave || undefined);
            onClose();
        } catch (err) {
            handleError('Unable to update incident. Please try again.', err as Error);
        }
    };

    return (
        <Formik initialValues={{}} onSubmit={handleSave}>
            <DialogWithForm
                dialogTitle={`Edit ${fieldLabel}`}
                isOpen={isOpen}
                secondaryButtonLabel="Cancel"
                onSecondaryButtonClick={onClose}
                primaryButtonLabel="Save"
                isPrimaryButtonDisabled={isSaveDisabled}
                dialogContent={
                    <>
                        <SuggestSingleSelect
                            id="edit-primaryImpactedServiceName"
                            name="edit-primaryImpactedServiceName"
                            label={fieldLabel}
                            disableAutoPopulate
                            values={impactedServiceNames}
                            selectedItem={selectValue}
                            onChange={(val) => {
                                setSelectValue(val ?? '');
                                if (val !== OTHER_OPTION) setOtherValue('');
                            }}
                        />
                        {selectValue === OTHER_OPTION && (
                            <div style={{ marginTop: 16 }}>
                                <TextInput
                                    id="edit-primaryImpactedServiceName-other"
                                    name="edit-primaryImpactedServiceName-other"
                                    label="Specify other"
                                    placeholderText="Specify other"
                                    value={otherValue}
                                    onChange={(e) => setOtherValue(e.target.value)}
                                    fill
                                />
                            </div>
                        )}
                    </>
                }
            />
        </Formik>
    );
};

const SeverityForm = ({
    fieldLabel,
    incident: _incident,
    isOpen,
    onClose,
    onSave,
    severityLevels,
    severityLevelNames,
    initialValue,
    onOpenSeverityLevelsModal,
}: {
    fieldLabel: string;
    incident: EditIncidentFieldModalProps['incident'];
    isOpen: boolean;
    onClose: () => void;
    onSave: (f: EditableFieldType, v: string | number | undefined) => Promise<void>;
    severityLevels: { id: number; name: string }[];
    severityLevelNames: string[];
    initialValue: string;
    onOpenSeverityLevelsModal: () => void;
}) => {
    const [selectValue, setSelectValue] = useState(initialValue);
    const level = severityLevels.find((s) => s.name === selectValue);
    const severityId = level?.id;
    const isSaveDisabled = severityId === undefined && selectValue !== '';

    const handleSave = async () => {
        if (severityId === undefined) return;
        try {
            await onSave('severity', severityId);
            onClose();
        } catch (err) {
            handleError('Unable to update incident. Please try again.', err as Error);
        }
    };

    return (
        <Formik initialValues={{}} onSubmit={handleSave}>
            <DialogWithForm
                dialogTitle={`Edit ${fieldLabel}`}
                isOpen={isOpen}
                secondaryButtonLabel="Cancel"
                onSecondaryButtonClick={onClose}
                primaryButtonLabel="Save"
                isPrimaryButtonDisabled={isSaveDisabled}
                dialogContent={
                    <>
                        <SuggestSingleSelect
                            id="edit-severity"
                            name="edit-severity"
                            label={fieldLabel}
                            disableAutoPopulate
                            values={['--', ...severityLevelNames]}
                            selectedItem={selectValue || '--'}
                            onChange={(val) => setSelectValue(val === '--' ? '' : (val ?? ''))}
                        />
                        <button
                            type="button"
                            onClick={onOpenSeverityLevelsModal}
                            className="link"
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                font: 'inherit',
                                cursor: 'pointer',
                                color: 'var(--color-link, #0066cc)',
                                textDecoration: 'underline',
                                marginTop: 16,
                            }}
                        >
                            View severity levels
                        </button>
                    </>
                }
            />
        </Formik>
    );
};

export const EditIncidentFieldModal = ({
    field,
    fieldLabel,
    incident,
    isOpen,
    onClose,
    onSave,
    missingQualityChecks,
    impactedServiceNames,
    severityLevels,
    severityLevelNames,
}: EditIncidentFieldModalProps) => {
    const [isSeverityLevelsModalOpen, setIsSeverityLevelsModalOpen] = useState(false);

    const getDialogContent = () => {
        switch (field) {
            case 'name':
                return (
                    <Formik
                        initialValues={{ name: incident.name }}
                        onSubmit={async (values) => {
                            try {
                                await onSave('name', values.name);
                                onClose();
                            } catch (err) {
                                handleError(
                                    'Unable to update incident. Please try again.',
                                    err as Error,
                                );
                            }
                        }}
                    >
                        {(formik) => (
                            <DialogWithForm
                                dialogTitle={`Edit ${fieldLabel}`}
                                isOpen={isOpen}
                                secondaryButtonLabel="Cancel"
                                onSecondaryButtonClick={() => {
                                    onClose();
                                    formik.resetForm();
                                }}
                                primaryButtonLabel="Save"
                                isPrimaryButtonDisabled={
                                    !formik.values.name?.trim() || formik.isSubmitting
                                }
                                dialogContent={
                                    <TextInputFormik id="name" label={fieldLabel} fill />
                                }
                            />
                        )}
                    </Formik>
                );
            case 'description':
                return (
                    <Formik
                        initialValues={{ description: incident.description }}
                        onSubmit={async (values) => {
                            try {
                                await onSave('description', values.description);
                                onClose();
                            } catch (err) {
                                handleError(
                                    'Unable to update incident. Please try again.',
                                    err as Error,
                                );
                            }
                        }}
                    >
                        {(formik) => (
                            <DialogWithForm
                                dialogTitle={`Edit ${fieldLabel}`}
                                isOpen={isOpen}
                                secondaryButtonLabel="Cancel"
                                onSecondaryButtonClick={() => {
                                    onClose();
                                    formik.resetForm();
                                }}
                                primaryButtonLabel="Save"
                                isPrimaryButtonDisabled={
                                    !formik.values.description?.trim() || formik.isSubmitting
                                }
                                dialogContent={
                                    <TextAreaFormik
                                        id="description"
                                        label={fieldLabel}
                                        fill
                                        autoResize={false}
                                    />
                                }
                            />
                        )}
                    </Formik>
                );
            case 'startedAt':
                return (
                    <Formik
                        initialValues={{
                            startedAt: incident.startedAt ? dayjs(incident.startedAt) : undefined,
                        }}
                        onSubmit={async (values) => {
                            try {
                                await onSave(
                                    'startedAt',
                                    values.startedAt
                                        ? dayjs(values.startedAt).toISOString()
                                        : undefined,
                                );
                                onClose();
                            } catch (err) {
                                handleError(
                                    'Unable to update incident. Please try again.',
                                    err as Error,
                                );
                            }
                        }}
                    >
                        {(formik) => (
                            <DialogWithForm
                                dialogTitle={`Edit ${fieldLabel}`}
                                isOpen={isOpen}
                                secondaryButtonLabel="Cancel"
                                onSecondaryButtonClick={() => {
                                    onClose();
                                    formik.resetForm();
                                }}
                                primaryButtonLabel="Save"
                                isPrimaryButtonDisabled={
                                    !formik.values.startedAt || formik.isSubmitting
                                }
                                dialogContent={
                                    <DateInputFormik
                                        id="startedAt"
                                        label={fieldLabel}
                                        showTime
                                        fill
                                    />
                                }
                            />
                        )}
                    </Formik>
                );
            case 'missingQualityCheck': {
                const initialSelect = getInitialSelectValue(
                    incident,
                    'missingQualityCheck',
                    missingQualityChecks,
                );
                const initialOther = getInitialOtherValue(
                    incident,
                    'missingQualityCheck',
                    missingQualityChecks,
                );
                return (
                    <MissingQualityCheckForm
                        fieldLabel={fieldLabel}
                        incident={incident}
                        isOpen={isOpen}
                        onClose={onClose}
                        onSave={onSave}
                        missingQualityChecks={missingQualityChecks}
                        initialSelect={initialSelect}
                        initialOther={initialOther}
                    />
                );
            }
            case 'primaryImpactedServiceName': {
                const initialSelect = getInitialSelectValue(
                    incident,
                    'primaryImpactedServiceName',
                    impactedServiceNames,
                );
                const initialOther = getInitialOtherValue(
                    incident,
                    'primaryImpactedServiceName',
                    impactedServiceNames,
                );
                return (
                    <PrimaryImpactedServiceForm
                        fieldLabel={fieldLabel}
                        incident={incident}
                        isOpen={isOpen}
                        onClose={onClose}
                        onSave={onSave}
                        impactedServiceNames={impactedServiceNames}
                        initialSelect={initialSelect}
                        initialOther={initialOther}
                    />
                );
            }
            case 'severity': {
                const initialValue =
                    severityLevels.find((s) => s.id === incident.severity)?.name ?? '';
                return (
                    <SeverityForm
                        fieldLabel={fieldLabel}
                        incident={incident}
                        isOpen={isOpen}
                        onClose={onClose}
                        onSave={onSave}
                        severityLevels={severityLevels}
                        severityLevelNames={severityLevelNames}
                        initialValue={initialValue}
                        onOpenSeverityLevelsModal={() => setIsSeverityLevelsModalOpen(true)}
                    />
                );
            }
            default:
                return null;
        }
    };

    return (
        <>
            {getDialogContent()}
            <SeverityLevelsInfoModal
                isOpen={isSeverityLevelsModalOpen}
                onClose={() => setIsSeverityLevelsModalOpen(false)}
            />
        </>
    );
};
